import { Children, Fragment, isValidElement, ReactNode } from "react";

/**
 * Groups children by their component's `displayName` property.
 *
 * This function is useful for creating components with named slots, where you want to
 * organize children based on their component type rather than their position.
 *
 * React Fragments are handled specially by default - when `ignoreFragments` is true,
 * the function recursively extracts children from Fragments and groups them as if the
 * Fragment wrapper didn't exist. This allows transparent use of Fragments in component
 * composition without affecting the grouping logic.
 *
 * @param children - The React children to group
 * @param ignoreFragments - When true (default), recursively extracts children from React Fragments
 *   and groups them normally. When false, treats Fragments as unnamed children. This is useful
 *   when you want to preserve Fragment boundaries or treat them as distinct elements.
 * @returns An object where:
 *   - Keys are component `displayName` values
 *   - Values are arrays of ReactNode elements matching that displayName
 *   - `_unnamed` key contains all children without a displayName or non-component children
 *
 * @example
 * Basic usage with named components:
 * ```tsx
 * const Header = () => <div>Header</div>;
 * Header.displayName = "Header";
 *
 * const Footer = () => <div>Footer</div>;
 * Footer.displayName = "Footer";
 *
 * function MyComponent({ children }: { children: ReactNode }) {
 *   const grouped = getChildrenDisplayNameMap(children);
 *
 *   return (
 *     <div>
 *       <header>{grouped.Header}</header>
 *       <main>{grouped._unnamed}</main>
 *       <footer>{grouped.Footer}</footer>
 *     </div>
 *   );
 * }
 *
 * // Usage:
 * <MyComponent>
 *   <Header />
 *   <p>This goes in main</p>
 *   <Footer />
 * </MyComponent>
 * ```
 *
 * @example
 * Fragment handling (default behavior):
 * ```tsx
 * // Fragments are transparently extracted by default
 * <MyComponent>
 *   <>
 *     <Header />
 *     <Footer />
 *   </>
 *   <p>Regular content</p>
 * </MyComponent>
 * // Result: grouped.Header, grouped.Footer, and grouped._unnamed = [<p>]
 * // The Fragment wrapper is ignored
 *
 * // Nested Fragments work recursively
 * <MyComponent>
 *   <>
 *     <>
 *       <Header />
 *     </>
 *     <Footer />
 *   </>
 * </MyComponent>
 * // Result: Both Header and Footer are extracted and grouped normally
 * ```
 *
 * @example
 * Preserving Fragment boundaries:
 * ```tsx
 * // When ignoreFragments is false, Fragments become unnamed children
 * const grouped = getChildrenDisplayNameMap(children, false);
 * // Fragments will appear in grouped._unnamed instead of being extracted
 * ```
 */
export const getChildrenDisplayNameMap = (children: ReactNode, ignoreFragments = true) => {
  return Children.toArray(children).reduce<{ _unnamed: ReactNode[] } & Record<string, ReactNode[]>>(
    (acc, child) => {
      // Ignore fragments
      if (ignoreFragments && isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
        const fragmentChildren = child.props.children;
        const fragmentMap = getChildrenDisplayNameMap(fragmentChildren, ignoreFragments);
        Object.entries(fragmentMap).forEach(([key, nodes]) => {
          const currentEntries = acc[key] ?? [];
          acc[key] = [...currentEntries, ...nodes];
        });
        return acc;
      }

      if (
        isValidElement(child) &&
        !!child.type &&
        typeof child.type === "function" &&
        "displayName" in child.type &&
        typeof child.type.displayName === "string" &&
        !!child.type.displayName
      ) {
        const currentEntries = acc[child.type.displayName] ?? [];
        acc[child.type.displayName] = [...currentEntries, child];
      } else {
        acc._unnamed.push(child);
      }
      return acc;
    },
    { _unnamed: [] }
  );
};
